import {
  resolveScheduledPostPreview,
  type ScheduledPostDraft,
  type ScheduledPostPreviewLogoAsset,
} from "./scheduled-post-preview";
import { applySocialPostVariantDraft } from "./social-post-variant-drafts";

export type AssistantSelectedAssetPreviewAsset = {
  generatedAssetId: string;
  blockId: string;
  role: string;
  position: number;
  slideIndex: number | null;
  slideId: string | null;
  objectKey: string;
  mimeType: string;
  signedUrl: string;
  width: number | null;
  height: number | null;
  prompt: string | null;
  createdAt: string;
  post: {
    output?: unknown;
    draft?: ScheduledPostDraft | null;
  } | null;
};

export function buildAssistantSelectedAssetLinkedAsset(asset: AssistantSelectedAssetPreviewAsset) {
  return {
    generatedAssetId: asset.generatedAssetId,
    blockId: asset.blockId,
    role: asset.role,
    position: asset.position,
    slideIndex: asset.slideIndex,
    slideId: asset.slideId,
    slideRole: null,
    objectKey: asset.objectKey,
    mimeType: asset.mimeType,
    signedUrl: asset.signedUrl,
    width: asset.width,
    height: asset.height,
    prompt: asset.prompt,
    createdAt: asset.createdAt,
  };
}

export function resolveAssistantSelectedAssetPreview({
  asset,
  logoAssets,
}: {
  asset: AssistantSelectedAssetPreviewAsset;
  logoAssets?: ScheduledPostPreviewLogoAsset[];
}) {
  if (!asset.post) {
    return null;
  }

  const draft = resolveAssetPreviewDraft(asset);
  const copyDraft = asset.post.draft ?? null;

  const preview = resolveScheduledPostPreview({
    output: asset.post.output ?? null,
    draft,
    selectedCarouselSlideId: asset.slideId,
    selectedCarouselSlideIndex: asset.slideIndex,
    logoAssets,
  });
  const selectedAsset = buildAssistantSelectedAssetLinkedAsset(asset);

  return {
    ...preview,
    caption: copyDraft?.caption?.trim() ? copyDraft.caption : preview.caption,
    hashtags: copyDraft ? copyDraft.hashtags : preview.hashtags,
    previewAsset:
      preview.previewAsset?.blockId === asset.blockId || !preview.previewAsset
        ? selectedAsset
        : preview.previewAsset,
    linkedAssets: [
      ...preview.linkedAssets.filter((linkedAsset) => linkedAsset.blockId !== asset.blockId),
      selectedAsset,
    ],
  };
}

function resolveAssetPreviewDraft(asset: AssistantSelectedAssetPreviewAsset) {
  const draft = asset.post?.draft ?? null;
  if (!draft) {
    return null;
  }

  const variantDraft = (draft.variantDrafts ?? []).find(
    (candidate) => candidate.generatedAssetId === asset.generatedAssetId,
  );
  if (variantDraft) {
    return applySocialPostVariantDraft(draft, asset.generatedAssetId);
  }

  const overrides =
    asset.slideId !== null || asset.slideIndex !== null
      ? (draft.carouselSlides.find(
          (slide) => slide.slideId === asset.slideId || slide.slideIndex === asset.slideIndex,
        )?.imageAssetOverrides ?? [])
      : draft.imageAssetOverrides;
  const currentGeneratedAssetId = overrides.find(
    (override) => override.blockId === asset.blockId,
  )?.generatedAssetId;

  return currentGeneratedAssetId && currentGeneratedAssetId !== asset.generatedAssetId
    ? null
    : draft;
}
